import { type Ref, useEffect, useImperativeHandle, useRef } from 'react';

// Public site key for app.vaartix.com. Development uses Cloudflare's test key, which always
// passes. VITE_TURNSTILE_SITE_KEY overrides both (set it at build time, like VITE_API_URL).
export const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ??
  (import.meta.env.DEV ? '1x00000000000000000000AA' : '0x4AAAAAAFsrs4rEt032x8E-');

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      theme: 'dark' | 'light' | 'auto';
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    },
  ): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** Load Cloudflare's script once for the whole app. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not load'));
    script.onerror = () => {
      loading = null;
      reject(new Error('Turnstile did not load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface TurnstileHandle {
  /** Get a fresh token: each one works once, so call this after every request that used one. */
  reset(): void;
}

/**
 * Cloudflare Turnstile. The widget's mode (Managed) is set on the site key in Cloudflare, so most
 * people see a quick check that passes by itself, and a checkbox only when Cloudflare is unsure.
 */
export function Turnstile({
  action,
  onToken,
  ref,
}: {
  action: string;
  /** The current token, or null while there isn't a usable one. */
  onToken: (token: string | null) => void;
  ref?: Ref<TurnstileHandle>;
}) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  // The latest callback, without re-rendering the widget when the parent re-renders.
  const tokenCallback = useRef(onToken);
  tokenCallback.current = onToken;

  useImperativeHandle(ref, () => ({
    reset() {
      tokenCallback.current(null);
      if (widget.current && window.turnstile) window.turnstile.reset(widget.current);
    },
  }));

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        widget.current = turnstile.render(container.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          theme: 'dark',
          callback: (token) => tokenCallback.current(token),
          'expired-callback': () => tokenCallback.current(null),
          'error-callback': () => tokenCallback.current(null),
        });
      })
      .catch(() => tokenCallback.current(null));
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [action]);

  return <div className="turnstile" ref={container} />;
}
